import { Clock } from 'lucide-react'
import React from 'react'

import { Badge } from 'slab'
import { type BrokersCoverageAuditLog } from 'utils/web_api.schemas'

interface CoverageAuditLogCardProps {
  log: BrokersCoverageAuditLog
}

const CoverageAuditLogCard: React.FC<CoverageAuditLogCardProps> = ({ log }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return {
      date: date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
      time: date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      }),
    }
  }

  const formatTargetType = (targetType: string | undefined) => {
    if (!targetType) return 'Unknown'
    const className = targetType.split('::').pop() || targetType

    switch (className) {
      case 'Premium':
        return log.target_details ? `Premium (${log.target_details})` : 'Premium'
      case 'Enrollment':
        return 'Enrollment'
      case 'EnrollmentCoverage':
        return log.target_details ? `Participant (${log.target_details})` : 'Participant Coverage'
      default:
        return className
    }
  }

  const formatReason = (reason: string) => {
    return reason
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const getActionBadgeType = (action: string): 'success' | 'info' | 'critical' | 'neutral' => {
    switch (action) {
      case 'create':
        return 'success'
      case 'update':
        return 'info'
      case 'destroy':
        return 'critical'
      default:
        return 'neutral'
    }
  }

  const formatValue = (value: unknown) => {
    if (value === null || value === undefined) {
      return <span className='text-gray-400 italic'>empty</span>
    }
    if (typeof value === 'boolean') {
      return <span className={value ? 'text-green-600' : 'text-red-600'}>{String(value)}</span>
    }
    if (typeof value === 'object') {
      return <code className='text-xs bg-gray-50 px-1 rounded'>{JSON.stringify(value)}</code>
    }
    return String(value)
  }

  const formatFieldName = (field: string) => {
    return field
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase())
  }

  const { date, time } = formatDate(log.created_at)
  const changes = log.audited_changes as Record<string, [unknown, unknown]>

  return (
    <div className='border rounded-lg bg-white p-4 hover:shadow-sm transition-shadow'>
      <div className='grid grid-cols-[220px_1fr] gap-4'>
        {/* Column 1: Date/Time, Target, and Changed By */}
        <div className='space-y-3'>
          <div className='flex items-start gap-2'>
            <Clock size={14} className='text-gray-400 mt-0.5' />
            <div className='text-sm'>
              <div className='font-medium text-gray-900'>{date}</div>
              <div className='text-xs text-gray-500'>{time}</div>
            </div>
          </div>

          <div className='text-sm'>
            <div className='text-xs text-gray-500 mb-1'>Target</div>
            <div className='font-medium text-gray-700'>{formatTargetType(log.target_type)}</div>
          </div>

          <div className='text-sm'>
            <div className='text-xs text-gray-500 mb-1'>Changed By</div>
            {log.created_by_broker
              ? (
                <div className='font-medium text-gray-700'>{log.created_by_broker.broker_name}</div>
                )
              : (
                <div className='text-gray-500 italic'>System</div>
                )}
          </div>

          {log.corrected_by_broker && (
            <div className='text-sm pt-2 border-t border-gray-100'>
              <div className='text-xs text-gray-500 mb-1'>Corrected By</div>
              <div className='font-medium text-gray-700'>{log.corrected_by_broker.broker_name}</div>
              {log.original_reason && (
                <div className='text-xs text-gray-500 mt-1'>
                  Original: {formatReason(log.original_reason)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Column 2: Reason and Changes */}
        <div className='space-y-2 border-l border-gray-100 pl-4'>
          <div className='flex items-center gap-2'>
            <Badge type={getActionBadgeType(log.action)}>
              {log.action.charAt(0).toUpperCase() + log.action.slice(1)}
            </Badge>
            <span className='text-sm text-gray-600'>{formatReason(log.reason)}</span>
          </div>

          {Object.keys(changes).length > 0 && (
            <div className='space-y-1'>
              {Object.entries(changes).map(([field, [oldVal, newVal]]) => (
                <div key={field} className='text-xs'>
                  <span className='font-medium text-gray-700'>{formatFieldName(field)}:</span>
                  <div className='ml-2 mt-0.5'>
                    <span className='inline-flex items-center gap-1'>
                      <span className='bg-red-50 px-1.5 py-0.5 rounded text-red-700'>
                        {formatValue(oldVal)}
                      </span>
                      <span className='text-gray-400'>→</span>
                      <span className='bg-green-50 px-1.5 py-0.5 rounded text-green-700'>
                        {formatValue(newVal)}
                      </span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CoverageAuditLogCard
